/* eslint-disable no-redeclare, @typescript-eslint/no-redeclare */
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Group,
  Indicator,
  List,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
/* eslint-enable no-redeclare, @typescript-eslint/no-redeclare */
import { IconArrowRight, IconCheck, IconChevronDown, IconCircleCheck, IconFlame } from '@tabler/icons';
import { FC, useContext, useMemo, useState } from 'react';
import PanePaper from '../../../Molecules/PanePaper';
import { ChargebeeS } from '../../../Service/ChargebeeS';
import { AuthContext, useJwtClaims } from '../../../Service/Context/AuthContext';
import { BillingInterval, BillingPlan } from '../../../Types/Billing';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { LicenceS } from '../../../Service/LicenceS';
import { BookitupSubscriptionContext } from '../../../Service/Context/BookitupSubscriptionContext';
import { useAppUser } from '../../../Service/Context/ApplicationUserContext';

interface Props {
  billingPlan: BillingPlan;
  billingInterval: BillingInterval;
}

const ACTIVE_BILLING_COLOR = '#FE8047';
const DEFAULT_VISIBLE_FEATURES = 7;

const Plan: FC<Props> = ({ billingPlan, billingInterval }) => {
  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const { refreshToken } = useContext(AuthContext);
  const jwtClaims = useJwtClaims();
  const { origin } = useAppUser();
  const { subscription, refresh: refreshSubscription, resourceAddon } = useContext(BookitupSubscriptionContext);
  const { refresh: refreshAddon } = useContext(ResourceAddonContext);
  const { name, internalName, free, popular, price, features, licence, description } = billingPlan;

  const { status, licence: subscriptionLicence, interval: subscriptionInterval } = subscription ?? {};
  const planIncompatible = resourceAddon && resourceAddon.interval !== billingInterval;
  const subscriptionExists = subscriptionLicence !== undefined && status !== 'CANCELLED';
  const isPlanActive = status !== 'CANCELLED' && licence === subscriptionLicence;
  const showPopularBadge = Boolean(popular);
  const showNewBadge = name === 'Plus';
  const isCurrentBillingInterval = billingInterval === subscriptionInterval;
  const canChangeBillingInterval = isPlanActive && !isCurrentBillingInterval && licence !== 'BASIC';
  const showPlanCheckoutButton = (!isPlanActive || canChangeBillingInterval) && licence !== 'BASIC';
  const visibleFeatures = features.slice(0, DEFAULT_VISIBLE_FEATURES);
  const additionalFeatures = features.slice(DEFAULT_VISIBLE_FEATURES);
  const getPlanCheckoutLabel = () => {
    if (canChangeBillingInterval) return 'Abrechnungsintervall ändern';
    if (subscriptionExists) return 'Plan ändern';
    return 'Plan buchen';
  };

  const { colors, colorScheme } = useMantineTheme();
  const [, , grayBorderColor] = colors.gray;
  const [, , , orangeBorderColor] = colors.orange;
  const [, , , blueBorderColor] = colors.blue;
  const [, , , , darkBorderColor] = colors.dark;
  let inactiveBorderColor = grayBorderColor;
  let cardShadow = popular ? '0 20px 48px rgba(254, 128, 71, 0.16)' : '0 12px 34px rgba(29, 81, 145, 0.08)';
  let cardAccent =
    colorScheme === 'dark'
      ? 'linear-gradient(90deg, #56616F 0%, #3F4854 100%)'
      : 'linear-gradient(90deg, #A8B0BA 0%, #7D8793 100%)';
  let ctaGradient = {
    from: colorScheme === 'dark' ? '#56616F' : '#A8B0BA',
    to: colorScheme === 'dark' ? '#3F4854' : '#7D8793',
    deg: 105,
  };
  let ctaColor = 'gray';
  let planFitLabel = 'Für mehr Struktur';

  if (colorScheme === 'dark') {
    inactiveBorderColor = showNewBadge ? colors.blue[5] : darkBorderColor;
    cardShadow = '0 12px 32px rgba(0, 0, 0, 0.16)';

    if (popular) {
      cardShadow = '0 18px 44px rgba(0, 0, 0, 0.26)';
    } else if (showNewBadge) {
      cardShadow = '0 18px 44px rgba(76, 139, 245, 0.18)';
    }
  } else if (popular) {
    inactiveBorderColor = orangeBorderColor;
  } else if (showNewBadge) {
    inactiveBorderColor = blueBorderColor;
    cardShadow = '0 18px 44px rgba(76, 139, 245, 0.14)';
  }

  if (free) {
    planFitLabel = 'Für den Einstieg';
  } else if (popular) {
    planFitLabel = 'Für mehr Automation';
  } else if (showNewBadge) {
    planFitLabel = 'Für mehr Flexibilität';
  }

  const cardBorder = isPlanActive ? `2px solid ${ACTIVE_BILLING_COLOR}` : `1px solid ${inactiveBorderColor}`;

  if (popular) {
    cardAccent = 'linear-gradient(90deg, #FE8047 0%, #F75D77 100%)';
    ctaGradient = { from: '#FE8047', to: '#F75D77', deg: 105 };
    ctaColor = 'orange';
  } else if (showNewBadge) {
    cardAccent = 'linear-gradient(90deg, #4C8BF5 0%, #8B5CF6 100%)';
    ctaGradient = { from: '#4C8BF5', to: '#8B5CF6', deg: 105 };
    ctaColor = 'blue';
  }

  const discount = useMemo(
    () => (billingInterval === 'Yearly' ? LicenceS.getAvailableDiscount(jwtClaims, name, origin) : null),
    [billingInterval, jwtClaims, name, origin],
  );

  const createOrUpdateSubscription = (coupon?: string) => {
    const planId = `${internalName}-EUR-${billingInterval}`;
    const onSuccess = () => {
      refreshToken();
      refreshAddon();
      refreshSubscription();
    };
    ChargebeeS.openHostedCheckout(planId, onSuccess, billingPlan, coupon);
  };

  const card = (
    <PanePaper
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        padding: 0,
        border: cardBorder,
        boxShadow: cardShadow,
      }}
    >
      <Box
        sx={{
          height: 5,
          background: cardAccent,
        }}
      />
      <Stack spacing={0} p="lg" sx={{ minHeight: '100%' }}>
        <Group position="apart" align="flex-start" spacing="sm" noWrap>
          <Box sx={{ minWidth: 0 }}>
            <Text size={10} color="dimmed" transform="uppercase" weight={800} sx={{ letterSpacing: 0.8 }}>
              {planFitLabel}
            </Text>
            <Text mt={3} weight={900} size={25} sx={{ lineHeight: 1.15 }}>
              {name}
            </Text>
          </Box>
          <Group spacing={6} sx={{ flex: '0 0 auto' }}>
            {showPopularBadge && (
              <Badge
                size="md"
                radius="sm"
                color="orange"
                variant={colorScheme === 'dark' ? 'light' : 'filled'}
                leftSection={<IconFlame size={14} stroke={2.2} />}
                sx={{ textTransform: 'none' }}
                styles={{
                  leftSection: { display: 'flex', alignItems: 'center' },
                  inner: { display: 'flex', alignItems: 'center', lineHeight: 1 },
                }}
              >
                Beliebt
              </Badge>
            )}
            {showNewBadge && (
              <Badge
                size="md"
                radius="sm"
                color="blue"
                variant={colorScheme === 'dark' ? 'light' : 'filled'}
                sx={{ textTransform: 'none' }}
              >
                Neu
              </Badge>
            )}
          </Group>
        </Group>

        <Text color="dimmed" mt="md" size="xs" sx={{ minHeight: 58, lineHeight: 1.55 }}>
          {description}
        </Text>

        <Box
          my="lg"
          p="md"
          sx={{
            borderRadius: 10,
            textAlign: 'center',
            backgroundColor: colorScheme === 'dark' ? colors.dark[7] : colors.gray[0],
          }}
        >
          {free ? (
            <>
              <Text weight={900} size={30} sx={{ lineHeight: 1 }}>
                0 €
              </Text>
              <Text mt={6} size="xs" color="dimmed">
                dauerhaft kostenlos
              </Text>
            </>
          ) : (
            <>
              {!discount && (
                <Group position="center" align="baseline" spacing={4} noWrap>
                  <Text weight={900} size={34} sx={{ lineHeight: 1 }}>
                    {price[billingInterval]} €
                  </Text>
                  <Text size="xs" color="dimmed">
                    / Monat
                  </Text>
                </Group>
              )}
              {discount && (
                <DiscountPrice
                  price={price[billingInterval]}
                  name={discount.dealName}
                  discountPercentage={discount.discountPercentage}
                />
              )}
              <Text mt={6} size={10} color="dimmed">
                zzgl. gesetzl. MwSt. · {billingInterval === 'Yearly' ? 'jährlich' : 'monatlich'} abgerechnet
              </Text>
            </>
          )}
        </Box>

        {!planIncompatible && showPlanCheckoutButton && (
          <Button
            fullWidth
            size="md"
            variant="gradient"
            gradient={ctaGradient}
            color={ctaColor}
            rightIcon={<IconArrowRight size={16} />}
            onClick={() => createOrUpdateSubscription(discount?.coupon)}
          >
            {getPlanCheckoutLabel()}
          </Button>
        )}
        {isPlanActive && !canChangeBillingInterval && (
          <Group position="center" spacing={6} sx={{ minHeight: 42, color: ACTIVE_BILLING_COLOR }}>
            <IconCircleCheck size={17} stroke={2.4} />
            <Text size="sm" weight={800}>
              Dein aktueller Plan
            </Text>
          </Group>
        )}
        {planIncompatible && (
          <Text
            size="xs"
            align="center"
            p="sm"
            sx={{
              borderRadius: 8,
              color: colorScheme === 'dark' ? colors.orange[2] : colors.orange[8],
              backgroundColor: colorScheme === 'dark' ? colors.dark[7] : colors.orange[0],
            }}
          >
            Wähle für Plan und Add-on dasselbe Abrechnungsintervall.
          </Text>
        )}

        <Divider my="lg" />
        <Text size="sm" weight={900}>
          {billingPlan.topFeaturesText}
        </Text>
        {billingPlan.topFeaturesComponent && <Box mt="sm">{billingPlan.topFeaturesComponent}</Box>}
        <List mt="sm" spacing={7} size="xs" icon={<IconCheck size={13} stroke={3} color="#FE8047" />}>
          {visibleFeatures.map((feature) => (
            <List.Item key={feature}>{feature}</List.Item>
          ))}
        </List>
        <Collapse in={featuresExpanded}>
          <List mt={7} spacing={7} size="xs" icon={<IconCheck size={13} stroke={3} color="#FE8047" />}>
            {additionalFeatures.map((feature) => (
              <List.Item key={feature}>{feature}</List.Item>
            ))}
          </List>
        </Collapse>
        {additionalFeatures.length > 0 && (
          <UnstyledButton
            type="button"
            aria-expanded={featuresExpanded}
            onClick={() => setFeaturesExpanded((current) => !current)}
            sx={(theme) => ({
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              color: theme.colorScheme === 'dark' ? theme.colors.gray[3] : theme.colors.gray[7],
              fontSize: 12,
              fontWeight: 800,
            })}
          >
            <IconChevronDown
              size={15}
              style={{ transform: featuresExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 160ms ease' }}
            />
            {featuresExpanded ? 'Weniger anzeigen' : `${additionalFeatures.length} weitere Funktionen`}
          </UnstyledButton>
        )}
      </Stack>
    </PanePaper>
  );

  return (
    <Indicator
      disabled={!isPlanActive}
      label={
        <Text color="white" size={11} weight="bold">
          Aktiv
        </Text>
      }
      position="top-center"
      radius="md"
      size={18}
      styles={(theme) => ({
        indicator: {
          backgroundColor: ACTIVE_BILLING_COLOR,
          color: theme.white,
          width: 50,
        },
      })}
      sx={{
        flex: '1 1 280px',
        maxWidth: 310,
        width: '100%',
        '@media (max-width: 576px)': {
          maxWidth: '100%',
        },
      }}
    >
      {card}
    </Indicator>
  );
};

const DiscountPrice: FC<{ price: number; name: string; discountPercentage: number }> = ({
  price,
  name,
  discountPercentage,
}) => {
  const discountedPrice = price * (1 - discountPercentage / 100);
  const formattedDiscountedPrice = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(discountedPrice);

  return (
    <Stack spacing={7} align="center">
      <Badge radius="sm" color="yellow" variant="filled" sx={{ color: '#2C2400', textTransform: 'none' }}>
        {name}
      </Badge>
      <Group position="center" align="baseline" spacing={4} noWrap>
        <Text weight={900} size={34} sx={{ lineHeight: 1 }}>
          {formattedDiscountedPrice} €
        </Text>
        <Text size="xs" color="dimmed">
          / Monat
        </Text>
      </Group>
      <Group spacing={6}>
        <Text size="xs" color="dimmed" td="line-through">
          {price} €
        </Text>
        <ThemeIcon radius="xl" size={20} color="green" variant="light">
          <IconCheck size={12} stroke={3} />
        </ThemeIcon>
        <Text size="xs" color="green" weight={800}>
          {discountPercentage}% günstiger
        </Text>
      </Group>
    </Stack>
  );
};

export default Plan;
